/**
 * hooks/usePagePermissions.js
 * VERSION 2.0.0 - With hierarchical permission support
 * 
 * Hook for checking page and action permissions on the frontend
 * Mirrors the backend permission checking logic
 */

import { useAppSelector } from './redux';
import { PERMISSION_PAGES } from '../utils/permissionConstants';

function usePagePermissions() {
  const { user } = useAppSelector(state => state.auth);
  const isTokenUser = !!user?.isTokenUser;

  const normalizeDynamicActionName = (actionName) => {
    if (!actionName || typeof actionName !== 'string') return null;

    const compact = actionName.toLowerCase().replace(/[_\s-]/g, '');
    const aliasMap = {
      view: 'view',
      create: 'create',
      edit: 'edit',
      delete: 'delete',
      checkin: 'checkin',
      checkout: 'checkout',
      viewpassword: 'viewPassword',
      editguest: 'editGuest',
      viewguestdetails: 'viewGuestDetails',
      exportdata: 'exportData',
      export: 'exportData',
    };

    return aliasMap[compact] || actionName;
  };

  const getDynamicPagePermissionsMap = () => {
    const rawPermissions = user?.dynamicPagePermissions;
    if (!rawPermissions) return {};
    if (rawPermissions instanceof Map) {
      return Object.fromEntries(rawPermissions.entries());
    }
    if (typeof rawPermissions === 'object') {
      return rawPermissions;
    }
    return {};
  };

  /**
   * Check if user can access a specific page
   * @param {string} pageName - Page identifier (devices, rooms, vouchers, etc.)
   * @returns {boolean} True if user can access the page
   */
  const canAccessPage = (pageName) => {
    // Admins have access to everything (except token-based sessions)
    if (!isTokenUser && user && ['super_admin', 'admin'].includes(user.role)) {
      return true;
    }

    // Check if page is enabled in user's permissions
    if (user?.pagePermissions?.[pageName]?.enabled) {
      return true;
    }

    return false;
  };

  /**
   * Check if user can perform a specific action on a page
   * @param {string} pageName - Page identifier
   * @param {string} actionName - Action identifier (view, create, edit, delete, etc.)
   * @returns {boolean} True if user can perform the action
   */
  const canPerformAction = (pageName, actionName) => {
    // Admins can perform all actions (except token-based sessions)
    if (!isTokenUser && user && ['super_admin', 'admin'].includes(user.role)) {
      return true;
    }

    // Check if user has this specific action allowed
    const actions = user?.pagePermissions?.[pageName]?.actions || [];
    return actions.some(a => a.action === actionName && a.allowed === true);
  };

  /**
   * Get all allowed actions for a specific page
   * @param {string} pageName - Page identifier
   * @returns {string[]} Array of allowed action names
   */
  const getAllowedActions = (pageName) => {
    // Admins have all actions (except token-based sessions)
    if (!isTokenUser && user && ['super_admin', 'admin'].includes(user.role)) {
      const pageConfig = PERMISSION_PAGES[pageName];
      return pageConfig ? Object.keys(pageConfig.actions) : [];
    }

    // Return allowed actions for regular users
    return (user?.pagePermissions?.[pageName]?.actions || [])
      .filter(a => a.allowed)
      .map(a => a.action);
  };

  /**
   * Get a summary of all user's page permissions
   * @returns {Object} Object containing all page permissions
   */
  const getPageAccessSummary = () => {
    if (!isTokenUser && user && ['super_admin', 'admin'].includes(user.role)) {
      return {
        isAdmin: true,
        hasFullAccess: true,
        allowedPages: ['devices', 'rooms', 'vouchers', 'users', 'branches', 'logs', 'reports', 'settings']
      };
    }

    const allowedPages = Object.entries(user?.pagePermissions || {})
      .filter(([_, perms]) => perms.enabled)
      .map(([page, _]) => page);

    return {
      isAdmin: false,
      hasFullAccess: false,
      allowedPages,
      permissions: user?.pagePermissions || {}
    };
  };

  /**
   * Check if user should see a specific button/action in UI
   * @param {string} pageName - Page identifier
   * @param {string} actionName - Action identifier
   * @returns {boolean} True if button should be visible
   */
  const shouldShowButton = (pageName, actionName) => {
    return canPerformAction(pageName, actionName);
  };

  const canAccessDynamicPage = (pageKey) => {
    if (!pageKey) return false;

    if (!isTokenUser && user && ['super_admin', 'admin'].includes(user.role)) {
      return true;
    }

    const dynamicPermissions = getDynamicPagePermissionsMap();
    return dynamicPermissions?.[pageKey]?.enabled === true;
  };

  const canPerformDynamicPageAction = (pageKey, actionName) => {
    if (!pageKey || !actionName) return false;

    if (!isTokenUser && user && ['super_admin', 'admin'].includes(user.role)) {
      return true;
    }

    const dynamicPermissions = getDynamicPagePermissionsMap();
    const actions = dynamicPermissions?.[pageKey]?.actions || [];
    const targetAction = normalizeDynamicActionName(actionName);
    return actions.some((action) => {
      const candidateAction = normalizeDynamicActionName(action?.action || action?.name);
      return candidateAction === targetAction && action.allowed === true;
    });
  };

  /**
   * Check if user is an admin
   * @returns {boolean} True if user is super_admin or admin
   */
  const isAdmin = () => {
    return !isTokenUser && user && ['super_admin', 'admin'].includes(user.role);
  };

  /**
   * Check if user is a branch operator
   * @returns {boolean} True if user is branchoperator
   */
  const isBranchOperator = () => {
    return user?.role === 'branchoperator';
  };

  /**
   * Check if user is a branch manager
   * @returns {boolean} True if user is branch_manager
   */
  const isBranchManager = () => {
    return user?.role === 'branch_manager';
  };

  return {
    canAccessPage,
    canPerformAction,
    getAllowedActions,
    getPageAccessSummary,
    shouldShowButton,
    canAccessDynamicPage,
    canPerformDynamicPageAction,
    isAdmin,
    isBranchOperator,
    isBranchManager,
    user
  };
}

export default usePagePermissions;
